import { useFileStore, FileEntry } from "@/stores/files.store";
import { useDeviceStore } from "@/stores/device.store";
import { useLogStore } from "@/stores/log.store";
import { useAppStore } from "@/stores/app.store";
import { useHistoryStore, OperationType, OperationStatus, OperationRecord } from "@/stores/history.store";
import { webAdbService } from "@/services/webadb.service";
import { fileSystemService } from "@/services/filesystem.service";

// 计算本地文件/目录的总字节数
async function calcLocalSize(handle: FileSystemHandle): Promise<number> {
  if (handle.kind === 'file') {
    const file = await (handle as FileSystemFileHandle).getFile();
    return file.size;
  }
  let total = 0;
  for await (const entry of (handle as FileSystemDirectoryHandle).values()) {
    total += await calcLocalSize(entry);
  }
  return total;
}

// 计算多个 FileEntry 的总字节数
async function calcTotalBytes(items: FileEntry[]): Promise<number> {
  let total = 0;
  for (const item of items) {
    if (item.handle) {
      total += await calcLocalSize(item.handle);
    } else {
      total += item.size;
    }
  }
  return total;
}

// 去重：如果选中了目录，则跳过该目录下的文件
function deduplicateItems(items: FileEntry[]): FileEntry[] {
  const folders = items.filter(item => item.type === 'directory').map(item => item.path);
  return items.filter(item => item.type === 'directory' || !folders.some(folder => item.path.startsWith(folder + '/')));
}

export const useFileOperations = () => {
  const { adb } = useDeviceStore();
  const { left, right, setFiles, setSearching, setRefreshing } = useFileStore();
  const { addLog } = useLogStore();
  const { setStatus, setProgress } = useAppStore();
  const { addRecord } = useHistoryStore();

  const refreshSide = async (side: 'left' | 'right') => {
    const sideState = side === 'left' ? left : right;
    if (!sideState.path) return;

    try {
      setRefreshing(side, true);
      if (sideState.path.startsWith('adb://')) {
        if (!adb) return;
        const adbPath = sideState.path.replace('adb://', '');
        const files = await webAdbService.findFiles(adb, adbPath, ['*']);
        setFiles(side, files);
      } else if (sideState.directoryHandle) {
        const files = await fileSystemService.readDirectory(sideState.directoryHandle);
        setFiles(side, files);
      }
    } catch (error: any) {
      addLog('error', `刷新失败: ${error.message}`);
    } finally {
      setRefreshing(side, false);
    }
  };

  const deleteSelected = async (side: 'left' | 'right') => {
    const sideState = side === 'left' ? left : right;
    if (sideState.selectedPaths.length === 0) return;

    const confirm = window.confirm(`确定要删除选中的 ${sideState.selectedPaths.length} 个项目吗？`);
    if (!confirm) return;

    try {
      setStatus('正在删除...');
      const records: OperationRecord[] = [];
      
      for (const path of sideState.selectedPaths) {
        const isAdb = path.startsWith('adb://') || sideState.path.startsWith('adb://');
        const file = sideState.files.find(f => f.path === path);
        
        if (isAdb) {
          if (!adb) continue;
          const adbPath = path.replace('adb://', '');
          await webAdbService.deletePath(adb, adbPath);
        } else if (sideState.directoryHandle) {
          const name = path.split('/').pop()!;
          await fileSystemService.deleteEntry(sideState.directoryHandle, name, true);
        }

        records.push({
          id: crypto.randomUUID(),
          type: OperationType.DELETE,
          timestamp: new Date(),
          status: OperationStatus.COMPLETED,
          details: {
            sourcePath: path,
            isDirectory: file?.type === 'directory',
            fileSize: file?.size
          }
        });
        
        addLog('success', `已删除: ${path}`);
      }

      if (records.length > 0) {
        if (records.length === 1) {
          addRecord(records[0]);
        } else {
          addRecord({
            id: crypto.randomUUID(),
            type: OperationType.BATCH_DELETE,
            timestamp: new Date(),
            status: OperationStatus.COMPLETED,
            details: { sourcePath: 'multiple' },
            children: records
          });
        }
      }

      await refreshSide(side);
      setStatus('删除完成');
    } catch (error: any) {
      addLog('error', `删除失败: ${error.message}`);
      setStatus('删除出错');
    }
  };

  const createFolder = async (side: 'left' | 'right') => {
    const sideState = side === 'left' ? left : right;
    if (!sideState.path) return;

    const name = window.prompt('请输入文件夹名称');
    if (!name) return;

    try {
      const fullPath = sideState.path.endsWith('/') ? sideState.path + name : sideState.path + '/' + name;
      
      if (sideState.path.startsWith('adb://')) {
        if (!adb) return;
        const adbPath = fullPath.replace('adb://', '');
        await webAdbService.createDirectory(adb, adbPath);
      } else if (sideState.directoryHandle) {
        await fileSystemService.createDirectory(sideState.directoryHandle, name);
      }

      addRecord({
        id: crypto.randomUUID(),
        type: OperationType.CREATE_FOLDER,
        timestamp: new Date(),
        status: OperationStatus.COMPLETED,
        details: {
          sourcePath: fullPath,
          isDirectory: true
        }
      });

      addLog('success', `已创建文件夹: ${name}`);
      await refreshSide(side);
    } catch (error: any) {
      addLog('error', `创建文件夹失败: ${error.message}`);
    }
  };

  const search = async (side: 'left' | 'right', query: string) => {
    const sideState = side === 'left' ? left : right;
    if (!sideState.path || !query) return;

    try {
      setSearching(side, true);
      setStatus('正在搜索...');
      
      if (sideState.path.startsWith('adb://')) {
        if (!adb) return;
        const adbPath = sideState.path.replace('adb://', '');
        
        // 支持逗号分隔的多个搜索规则
        const rules = query.split(',').map(r => r.trim()).filter(Boolean);
        // 如果规则不包含通配符，自动添加
        const processedRules = rules.map(r => r.includes('*') ? r : `*${r}*`);
        
        addLog('info', `正在搜索: ${processedRules.join(', ')}`);
        const results = await webAdbService.findFiles(adb, adbPath, processedRules, true);
        setFiles(side, results);
      } else if (sideState.directoryHandle) {
        addLog('info', `正在搜索: ${query}`);
        const results = await fileSystemService.searchFiles(sideState.directoryHandle, query);
        setFiles(side, results);
      }
      setStatus('搜索完成');
    } catch (error: any) {
      addLog('error', `搜索失败: ${error.message}`);
      setStatus('搜索出错');
    } finally {
      setSearching(side, false);
    }
  };

  const transferSelected = async (fromSide: 'left' | 'right') => {
    const toSide = fromSide === 'left' ? 'right' : 'left';
    const fromState = fromSide === 'left' ? left : right;
    const toState = toSide === 'left' ? left : right;

    if (fromState.selectedPaths.length === 0 || !toState.path) {
      if (!toState.path) addLog('warning', '目标面板未选择路径');
      return;
    }

    const isFromAdb = fromState.path.startsWith('adb://');
    const isToAdb = toState.path.startsWith('adb://');

    try {
      if (isFromAdb && !isToAdb) {
        // ADB -> Local
        if (!adb || !toState.directoryHandle) return;
        const selectedItems = fromState.files.filter(f => fromState.selectedPaths.includes(f.path));
        const deduplicatedItems = deduplicateItems(selectedItems);
        if (selectedItems.length !== deduplicatedItems.length) {
          addLog('info', `智能去重: 跳过 ${selectedItems.length - deduplicatedItems.length} 个已包含在目录中的文件`);
        }
        const startTime = Date.now();
        let totalBytes = 0;

        // 单文件直接下载，跳过并行开销
        if (deduplicatedItems.length === 1 && deduplicatedItems[0].type === 'file') {
          const item = deduplicatedItems[0];
          totalBytes = await webAdbService.downloadFileToLocal(
            adb, item.path, toState.directoryHandle, item.name,
            (bytes) => {
              const mb = (bytes / 1024 / 1024).toFixed(1);
              const elapsed = (Date.now() - startTime) / 1000;
              const speed = elapsed > 0 ? (bytes / 1024 / 1024 / elapsed).toFixed(1) : '0';
              setStatus(`下载中: ${mb} MB (${speed} MB/s)`);
            }
          );
        } else {
          // 多文件/目录使用并行模式
          addLog('info', `使用并行下载模式下载 ${deduplicatedItems.length} 个项目...`);
          totalBytes = await webAdbService.downloadFilesParallel(
            adb, deduplicatedItems, toState.directoryHandle,
            (current, total, bytes) => {
              const mb = (bytes / 1024 / 1024).toFixed(1);
              const elapsed = (Date.now() - startTime) / 1000;
              const speed = bytes > 0 && elapsed > 0 ? (bytes / 1024 / 1024 / elapsed).toFixed(1) : '0';
              setStatus(`正在下载: ${current}/${total} 文件, ${mb} MB (${speed} MB/s)`);
              setProgress((current / total) * 100);
            }
          );
        }

        const elapsed = ((Date.now() - startTime) / 1000).toFixed(1);
        const avgSpeed = (totalBytes / 1024 / 1024 / parseFloat(elapsed)).toFixed(1);
        addLog('success', `传输完成: ${(totalBytes / 1024 / 1024).toFixed(2)} MB, 耗时 ${elapsed}秒, 平均速度 ${avgSpeed} MB/s`);

        addRecord({
          id: crypto.randomUUID(),
          type: OperationType.BATCH_COPY,
          timestamp: new Date(),
          status: OperationStatus.COMPLETED,
          details: {
            sourcePath: 'multiple',
            targetPath: toState.path
          },
          children: deduplicatedItems.map(item => ({
            id: crypto.randomUUID(),
            type: OperationType.COPY,
            timestamp: new Date(),
            status: OperationStatus.COMPLETED,
            details: {
              sourcePath: item.path,
              targetPath: toState.path + '/' + item.name,
              fileSize: item.size
            }
          }))
        });
      } else if (!isFromAdb && isToAdb) {
        // Local -> ADB
        if (!adb) return;
        const destPath = toState.path.replace('adb://', '');
        const selectedItems = fromState.files.filter(f => fromState.selectedPaths.includes(f.path));
        
        setStatus('正在准备上传到设备...');
        const records: OperationRecord[] = [];

        for (const item of selectedItems) {
          if (item.type === 'file' && item.handle instanceof FileSystemFileHandle) {
            const file = await item.handle.getFile();
            await webAdbService.uploadFile(adb, destPath, file);
            
            records.push({
              id: crypto.randomUUID(),
              type: OperationType.COPY,
              timestamp: new Date(),
              status: OperationStatus.COMPLETED,
              details: {
                sourcePath: item.path,
                targetPath: toState.path + '/' + item.name,
                fileSize: item.size
              }
            });

            addLog('success', `已上传: ${item.name}`);
          } else if (item.type === 'directory' && item.handle instanceof FileSystemDirectoryHandle) {
            await webAdbService.uploadDirectory(adb, destPath, item.handle);
            
            records.push({
              id: crypto.randomUUID(),
              type: OperationType.COPY,
              timestamp: new Date(),
              status: OperationStatus.COMPLETED,
              details: {
                sourcePath: item.path,
                targetPath: toState.path + '/' + item.name,
                isDirectory: true
              }
            });
            addLog('success', `已同步目录: ${item.name}`);
          }
        }

        if (records.length > 0) {
          addRecord({
            id: crypto.randomUUID(),
            type: OperationType.BATCH_COPY,
            timestamp: new Date(),
            status: OperationStatus.COMPLETED,
            details: { sourcePath: 'local', targetPath: toState.path },
            children: records
          });
        }

        addLog('success', `传输完成`);
      } else if (!isFromAdb && !isToAdb) {
        // Local -> Local (并行高速复制，按字节显示进度)
        if (!toState.directoryHandle) return;
        setStatus('正在计算文件大小...');
        const selectedItems = fromState.files.filter(f => fromState.selectedPaths.includes(f.path) && f.handle);

        const totalBytes = await calcTotalBytes(selectedItems);
        let transferred = 0;
        const startTime = Date.now();

        setStatus('正在本地复制...');
        const copyTasks = selectedItems.map(item => ({
          handle: item.handle!,
          targetDir: toState.directoryHandle!
        }));

        await fileSystemService.copyEntriesParallel(copyTasks, (bytes) => {
          transferred += bytes;
          const pct = totalBytes > 0 ? (transferred / totalBytes) * 100 : 0;
          const mb = (transferred / 1024 / 1024).toFixed(1);
          const totalMb = (totalBytes / 1024 / 1024).toFixed(1);
          const elapsed = (Date.now() - startTime) / 1000;
          const speed = elapsed > 0 ? (transferred / 1024 / 1024 / elapsed).toFixed(1) : '0';
          setProgress(pct);
          setStatus(`本地复制: ${mb}/${totalMb} MB (${speed} MB/s)`);
        });

        const elapsed = ((Date.now() - startTime) / 1000).toFixed(1);
        addLog('success', `本地传输完成: ${(totalBytes / 1024 / 1024).toFixed(2)} MB, 耗时 ${elapsed}秒`);
      } else {
        // ADB -> ADB
        if (!adb) return;
        const toPath = toState.path.replace('adb://', '');
        setStatus('正在设备内复制...');
        const selectedItems = fromState.files.filter(f => fromState.selectedPaths.includes(f.path));

        for (const item of selectedItems) {
          const itemPath = item.path.replace('adb://', '');
          const destPath = toPath + (toPath.endsWith('/') ? '' : '/') + item.name;
          await webAdbService.executeShell(adb, `cp -r "${itemPath}" "${destPath}"`);
          addLog('success', `已复制: ${item.name}`);
        }
        addLog('success', '设备内传输完成');
      }
      await refreshSide(toSide);
    } catch (error: any) {
      addLog('error', `传输失败: ${error.message}`);
    } finally {
      setStatus('就绪');
    }
  };

  // 映射复制：按行索引对应复制，左边文件/文件夹复制到右边对应行的文件夹中（可选指定子目录）
  const mappedTransfer = async (subdirectoryName: string = '') => {
    const sourceItems = left.files; // 包含文件和文件夹
    const targetDirs = right.files.filter(f => f.type === 'directory');

    if (sourceItems.length === 0) {
      addLog('warning', '左侧没有项目可复制');
      return;
    }
    if (targetDirs.length === 0) {
      addLog('warning', '右侧没有目标文件夹');
      return;
    }

    const isLeftAdb = left.path.startsWith('adb://');
    const isRightAdb = right.path.startsWith('adb://');

    // 详细日志
    addLog('info', `左侧路径: ${left.path}, 类型: ${isLeftAdb ? 'ADB' : 'Local'}`);
    addLog('info', `右侧路径: ${right.path}, 类型: ${isRightAdb ? 'ADB' : 'Local'}`);
    addLog('info', `左侧项目: ${sourceItems.length} 个 (文件: ${sourceItems.filter(f => f.type === 'file').length}, 目录: ${sourceItems.filter(f => f.type === 'directory').length})`);
    addLog('info', `右侧目录: ${targetDirs.length} 个`);
    if (subdirectoryName) {
      addLog('info', `目标子目录: ${subdirectoryName}`);
    }

    const pairCount = Math.min(sourceItems.length, targetDirs.length);
    const targetDesc = subdirectoryName ? `${pairCount} 对项目-文件夹的 ${subdirectoryName} 目录` : `${pairCount} 对项目-文件夹`;
    addLog('info', `开始映射复制: ${targetDesc}`);
    setStatus(`正在映射复制 ${pairCount} 个项目...`);

    try {
      // Local -> Local 并行复制（按字节显示进度）
      if (!isLeftAdb && !isRightAdb && left.directoryHandle && right.directoryHandle) {
        // 先计算总字节数
        setStatus('正在计算文件大小...');
        const itemsToCalc = sourceItems.slice(0, pairCount).filter(item => item.handle);
        const totalBytes = await calcTotalBytes(itemsToCalc);
        let transferred = 0;
        const startTime = Date.now();

        const CONCURRENCY = navigator.hardwareConcurrency || 4;
        const active = new Set<Promise<void>>();

        for (let i = 0; i < pairCount; i++) {
          const sourceItem = sourceItems[i];
          const targetDir = targetDirs[i];

          if (!sourceItem.handle || !targetDir.handle) continue;

          const task = (async () => {
            try {
              const freshSourceHandle = sourceItem.type === 'directory'
                ? await left.directoryHandle!.getDirectoryHandle(sourceItem.name)
                : await left.directoryHandle!.getFileHandle(sourceItem.name);
              const freshTargetDirHandle = await right.directoryHandle!.getDirectoryHandle(targetDir.name);
              // 如果指定了子目录，则创建子目录；否则直接使用目标文件夹
              const finalTargetHandle = subdirectoryName
                ? await freshTargetDirHandle.getDirectoryHandle(subdirectoryName, { create: true })
                : freshTargetDirHandle;
              const targetDisplayPath = subdirectoryName ? `${targetDir.name}/${subdirectoryName}` : targetDir.name;

              await fileSystemService.copyEntry(freshSourceHandle, finalTargetHandle, undefined, '', (bytes) => {
                transferred += bytes;
                const pct = totalBytes > 0 ? (transferred / totalBytes) * 100 : 0;
                const mb = (transferred / 1024 / 1024).toFixed(1);
                const totalMb = (totalBytes / 1024 / 1024).toFixed(1);
                const elapsed = (Date.now() - startTime) / 1000;
                const speed = elapsed > 0 ? (transferred / 1024 / 1024 / elapsed).toFixed(1) : '0';
                setProgress(pct);
                setStatus(`映射复制: ${mb}/${totalMb} MB (${speed} MB/s)`);
              });
              addLog('success', `${sourceItem.name} -> ${targetDisplayPath}/`);
            } catch (e: any) {
              addLog('error', `复制 ${sourceItem.name} 失败: ${e.message}`);
              throw e;
            }
          })().finally(() => active.delete(task));

          active.add(task);
          if (active.size >= CONCURRENCY) {
            await Promise.race(active);
          }
        }
        await Promise.all(active);

        const elapsed = ((Date.now() - startTime) / 1000).toFixed(1);
        addLog('success', `映射复制完成: ${(totalBytes / 1024 / 1024).toFixed(2)} MB, 耗时 ${elapsed}秒`);
      } else {
        // 其他情况保持顺序处理
        for (let i = 0; i < pairCount; i++) {
          const sourceItem = sourceItems[i];
          const targetDir = targetDirs[i];
          const isSourceDir = sourceItem.type === 'directory';
          const targetDisplayPath = subdirectoryName ? `${targetDir.name}/${subdirectoryName}` : targetDir.name;

          setStatus(`正在复制 [${i + 1}/${pairCount}]: ${sourceItem.name} -> ${targetDisplayPath}/`);

          if (!isLeftAdb && !sourceItem.handle) continue;
          if (!isRightAdb && !targetDir.handle) continue;

          if (isLeftAdb && isRightAdb) {
            if (!adb) continue;
            // 如果指定了子目录，先创建子目录
            const targetPath = subdirectoryName ? targetDir.path + '/' + subdirectoryName : targetDir.path;
            if (subdirectoryName) {
              await webAdbService.createDirectory(adb, targetPath);
            }
            const destPath = targetPath + '/' + sourceItem.name;
            await webAdbService.executeShell(adb, `cp -r "${sourceItem.path}" "${destPath}"`);
          } else if (isLeftAdb && !isRightAdb) {
            if (!adb || !right.directoryHandle) continue;
            const targetDirHandle = await right.directoryHandle.getDirectoryHandle(targetDir.name);
            // 如果指定了子目录，则创建子目录；否则直接使用目标文件夹
            const finalTargetHandle = subdirectoryName
              ? await targetDirHandle.getDirectoryHandle(subdirectoryName, { create: true })
              : targetDirHandle;
            if (isSourceDir) {
              await webAdbService.downloadDirectoryToLocal(adb, sourceItem.path, finalTargetHandle, sourceItem.name);
            } else {
              await webAdbService.downloadFileToLocal(adb, sourceItem.path, finalTargetHandle, sourceItem.name);
            }
          } else if (!isLeftAdb && isRightAdb) {
            if (!adb || !sourceItem.handle) continue;
            // 如果指定了子目录，先创建子目录路径
            const targetPath = subdirectoryName ? targetDir.path + '/' + subdirectoryName : targetDir.path;
            if (subdirectoryName) {
              await webAdbService.createDirectory(adb, targetPath);
            }
            if (isSourceDir) {
              await webAdbService.uploadDirectory(adb, targetPath, sourceItem.handle as FileSystemDirectoryHandle);
            } else {
              const file = await (sourceItem.handle as FileSystemFileHandle).getFile();
              await webAdbService.uploadFile(adb, targetPath, file);
            }
          }

          setProgress(((i + 1) / pairCount) * 100);
          addLog('success', `[${i + 1}/${pairCount}] ${sourceItem.name} -> ${targetDisplayPath}/`);
        }
      }

      addLog('success', `映射复制完成: ${pairCount} 个项目`);
    } catch (error: any) {
      addLog('error', `映射复制失败: ${error.message}`);
    } finally {
      setStatus('就绪');
    }
  };

  // 批量分发：将左侧选中的文件分发到右侧所有文件夹（可选指定子目录）
  const batchDistribute = async (subdirectoryName: string) => {
    const sourceState = left;
    const targetState = right;

    if (sourceState.selectedPaths.length === 0) {
      addLog('warning', '请先在左侧选择要分发的文件');
      return;
    }

    const targetDirs = targetState.files.filter(f => f.type === 'directory');
    if (targetDirs.length === 0) {
      addLog('warning', '右侧没有目标文件夹');
      return;
    }

    const selectedItems = sourceState.files.filter(f => sourceState.selectedPaths.includes(f.path));
    if (selectedItems.length === 0) return;

    const isLeftAdb = sourceState.path.startsWith('adb://');
    const isRightAdb = targetState.path.startsWith('adb://');

    const targetDesc = subdirectoryName ? `${targetDirs.length} 个文件夹的 ${subdirectoryName} 目录` : `${targetDirs.length} 个文件夹`;
    addLog('info', `开始批量分发: ${selectedItems.length} 个文件 -> ${targetDesc}`);

    try {
      setStatus('正在批量分发...');

      // Local -> Local 分发
      if (!isLeftAdb && !isRightAdb && sourceState.directoryHandle && targetState.directoryHandle) {
        const startTime = Date.now();

        for (const targetDir of targetDirs) {
          if (!targetDir.handle) continue;

          // 获取目标文件夹的实际 handle
          const targetDirHandle = await targetState.directoryHandle.getDirectoryHandle(targetDir.name);
          // 如果指定了子目录，则创建子目录；否则直接使用目标文件夹
          const finalTargetHandle = subdirectoryName
            ? await targetDirHandle.getDirectoryHandle(subdirectoryName, { create: true })
            : targetDirHandle;

          const targetDisplayPath = subdirectoryName ? `${targetDir.name}/${subdirectoryName}` : targetDir.name;
          addLog('info', `分发到: ${targetDisplayPath}`);

          for (const item of selectedItems) {
            if (!item.handle) continue;

            // 获取源文件/文件夹的 handle
            const sourceHandle = item.type === 'directory'
              ? await sourceState.directoryHandle.getDirectoryHandle(item.name)
              : await sourceState.directoryHandle.getFileHandle(item.name);

            // 计算文件大小
            if (item.type === 'file') {
              await (sourceHandle as FileSystemFileHandle).getFile();
            }

            // 复制到目标目录
            await fileSystemService.copyEntry(sourceHandle, finalTargetHandle);
            addLog('success', `已分发: ${item.name} -> ${targetDisplayPath}`);
          }
        }

        const elapsed = ((Date.now() - startTime) / 1000).toFixed(1);
        addLog('success', `批量分发完成: ${selectedItems.length} 个文件已分发到 ${targetDesc}，耗时 ${elapsed}秒`);
      } else if (!isLeftAdb && isRightAdb) {
        // Local -> ADB 分发
        if (!adb) return;

        for (const targetDir of targetDirs) {
          // 如果指定了子目录，则创建子目录路径；否则直接使用目标文件夹路径
          const targetPath = subdirectoryName ? targetDir.path + '/' + subdirectoryName : targetDir.path;
          if (subdirectoryName) {
            await webAdbService.createDirectory(adb, targetPath);
          }

          addLog('info', `分发到: ${targetPath}`);

          for (const item of selectedItems) {
            if (!item.handle) continue;

            if (item.type === 'file') {
              const file = await (item.handle as FileSystemFileHandle).getFile();
              await webAdbService.uploadFile(adb, targetPath, file);
              addLog('success', `已分发: ${item.name} -> ${targetPath}`);
            } else {
              await webAdbService.uploadDirectory(adb, targetPath, item.handle as FileSystemDirectoryHandle);
              addLog('success', `已分发目录: ${item.name} -> ${targetPath}`);
            }
          }
        }

        addLog('success', `批量分发完成: ${selectedItems.length} 个文件已分发到 ${targetDesc}`);
      } else if (isLeftAdb && !isRightAdb) {
        // ADB -> Local 分发
        if (!adb || !targetState.directoryHandle) return;

        for (const targetDir of targetDirs) {
          if (!targetDir.handle) continue;

          const targetDirHandle = await targetState.directoryHandle.getDirectoryHandle(targetDir.name);
          // 如果指定了子目录，则创建子目录；否则直接使用目标文件夹
          const finalTargetHandle = subdirectoryName
            ? await targetDirHandle.getDirectoryHandle(subdirectoryName, { create: true })
            : targetDirHandle;

          const targetDisplayPath = subdirectoryName ? `${targetDir.name}/${subdirectoryName}` : targetDir.name;
          addLog('info', `分发到: ${targetDisplayPath}`);

          for (const item of selectedItems) {
            if (item.type === 'file') {
              await webAdbService.downloadFileToLocal(adb, item.path, finalTargetHandle, item.name);
              addLog('success', `已分发: ${item.name} -> ${targetDisplayPath}`);
            } else {
              await webAdbService.downloadDirectoryToLocal(adb, item.path, finalTargetHandle, item.name);
              addLog('success', `已分发目录: ${item.name} -> ${targetDisplayPath}`);
            }
          }
        }

        addLog('success', `批量分发完成: ${selectedItems.length} 个文件已分发到 ${targetDesc}`);
      } else if (isLeftAdb && isRightAdb) {
        // ADB -> ADB 分发
        if (!adb) return;

        for (const targetDir of targetDirs) {
          // 如果指定了子目录，则创建子目录路径；否则直接使用目标文件夹路径
          const targetPath = subdirectoryName ? targetDir.path + '/' + subdirectoryName : targetDir.path;
          if (subdirectoryName) {
            await webAdbService.createDirectory(adb, targetPath);
          }

          addLog('info', `分发到: ${targetPath}`);

          for (const item of selectedItems) {
            const destPath = targetPath + '/' + item.name;
            await webAdbService.executeShell(adb, `cp -r "${item.path}" "${destPath}"`);
            addLog('success', `已分发: ${item.name} -> ${targetPath}`);
          }
        }

        addLog('success', `批量分发完成: ${selectedItems.length} 个文件已分发到 ${targetDesc}`);
      }

    } catch (error: any) {
      addLog('error', `批量分发失败: ${error.message}`);
    } finally {
      setStatus('就绪');
    }
  };

  return {
    refreshSide,
    deleteSelected,
    createFolder,
    transferSelected,
    mappedTransfer,
    batchDistribute,
    search
  };
};
